import { Controller } from "@hotwired/stimulus"

const TAB_IN_ANCHOR = /-(input|settings)(?:-|$)/
const COMMENT_LINK = "a.review-comment-item--clickable"

export default class extends Controller {
  static values = {
    highlightMs: { type: Number, default: 1600 },
    maxWaitMs:   { type: Number, default: 8000 }
  }

  connect() {
    this._pendingAnchor = this.anchorId(window.location.hash)
    this._onHashChange = () => this.handleHash(window.location.hash, { reset: true })
    this._onFrameLoad = () => this.revealFromLocation()
    this._onRender = () => this.revealFromLocation()
    this._onAnchorReady = () => this.retryIfPending(true)
    this._onCommentClick = (event) => this.interceptCommentClick(event)

    window.addEventListener("hashchange", this._onHashChange)
    document.addEventListener("turbo:frame-load", this._onFrameLoad)
    document.addEventListener("turbo:render", this._onRender)
    document.addEventListener("turbo:load", this._onRender)
    document.addEventListener("click", this._onCommentClick, true)
    window.addEventListener("anchor:ready", this._onAnchorReady)

    if (this._pendingAnchor) this.handleHash(`#${this._pendingAnchor}`)
  }

  disconnect() {
    window.removeEventListener("hashchange", this._onHashChange)
    document.removeEventListener("turbo:frame-load", this._onFrameLoad)
    document.removeEventListener("turbo:render", this._onRender)
    document.removeEventListener("turbo:load", this._onRender)
    document.removeEventListener("click", this._onCommentClick, true)
    window.removeEventListener("anchor:ready", this._onAnchorReady)
  }

  interceptCommentClick(event) {
    if (event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const link = event.target.closest(COMMENT_LINK)
    if (!link) return

    const url = new URL(link.href, window.location.origin)
    const id = this.anchorId(url.hash)
    if (!id) return

    this._pendingAnchor = id
    if (url.pathname !== window.location.pathname) return

    event.preventDefault()
    event.stopImmediatePropagation()
    this.handleHash(url.hash, { reset: true })
    if (window.location.hash !== url.hash) {
      history.replaceState(history.state, "", url.pathname + url.search + url.hash)
    }
  }

  revealFromLocation() {
    const id = this.anchorId(window.location.hash) || this._pendingAnchor
    if (id) this.handleHash(`#${id}`)
    this.retryIfPending()
  }

  async handleHash(hash, { reset = false } = {}) {
    const id = this.anchorId(hash)
    if (!id) return
    if (reset) this._pendingAnchor = id

    this._pendingAnchor = id
    this.openTab(id)

    const ok = await this.waitForAndReveal(id, this.maxWaitMsValue)
    if (ok) this._pendingAnchor = null
  }

  async retryIfPending(force = false) {
    if (!this._pendingAnchor) return
    const ok = await this.waitForAndReveal(this._pendingAnchor, force ? 200 : 600)
    if (ok) this._pendingAnchor = null
  }

  async waitForAndReveal(anchorId, maxMs) {
    const start = performance.now()
    let interval = 80

    const first = document.getElementById(anchorId)
    if (first) { this.reveal(first); return true }

    while ((performance.now() - start) < maxMs) {
      await this.sleep(interval)
      const el = document.getElementById(anchorId)
      if (el) { this.reveal(el); return true }
      if (interval < 300) interval += 40
    }
    return false
  }

  openTab(anchorId) {
    const match = anchorId.match(TAB_IN_ANCHOR)
    const tabName = match?.[1]
    if (!tabName) return

    this.showTab(tabName)
  }

  reveal(el) {
    const details = el.closest("details")
    if (details && !details.open) details.open = true

    const tabpanel = el.closest("[data-tabs-target='tab']") || el.closest("[role='tabpanel']")
    if (tabpanel?.id) this.showTab(tabpanel.id)

    el.scrollIntoView({ behavior: "smooth", block: "center" })
    el.classList.remove("anchor-target--highlight")
    void el.offsetWidth
    el.classList.add("anchor-target--highlight")
    clearTimeout(this._highlightTimer)
    this._highlightTimer = setTimeout(
      () => el.classList.remove("anchor-target--highlight"),
      this.highlightMsValue
    )
    try { el.focus({ preventScroll: true }) } catch (_) { /* focus is best-effort */ }
  }

  showTab(tabName) {
    const panel = document.getElementById(tabName)
    if (panel && !panel.hidden) return

    this.tabButton(tabName)?.click()
  }

  tabButton(tabName) {
    return (
      document.querySelector(`[data-tabs-tab-id-param="${tabName}"]`) ||
      document.querySelector(`[role="tab"][aria-controls][data-for-tab="${tabName}"]`) ||
      document.querySelector(`[data-for-tab="${tabName}"]`) ||
      document.querySelector(`[role="tab"][aria-controls*="${tabName}"]`)
    )
  }

  anchorId(hash) {
    return decodeURIComponent((hash || "").replace(/^#/, ""))
  }

  sleep(ms) { return new Promise(r => setTimeout(r, ms)) }
}
